import React, { useState, useEffect, useRef } from 'react';
import { Input } from 'rizzui';

interface ControlledInputProps {
    id?: string;
    type?: "number" | "text" | "search" | "email" | "tel" | "url" | "time" | "date" | "week" | "month" | "datetime-local";
    label?: string;
    placeholder?: string;
    className?: string;
    inputClassName?: string;
    value: string;
    onChange: (value: string) => void;
    debounceMs?: number;
}

const ControlledInput: React.FC<ControlledInputProps> = ({
    id,
    type = "text",
    label,
    placeholder,
    className,
    inputClassName,
    value,
    onChange,
    debounceMs = 0
}) => {
    const [localValue, setLocalValue] = useState(value);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Sincronizar valor local con prop value cuando cambie externamente
    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setLocalValue(newValue);

        // Limpiar timeout anterior
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        // Si hay debounce, esperar antes de llamar onChange
        if (debounceMs > 0) {
            timeoutRef.current = setTimeout(() => {
                onChange(newValue);
            }, debounceMs);
        } else {
            // Sin debounce, llamar inmediatamente
            onChange(newValue);
        }
    };

    // Limpiar timeout al desmontar
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return (
        <Input
            ref={inputRef}
            id={id}
            type={type}
            label={label}
            placeholder={placeholder}
            className={className}
            inputClassName={inputClassName}
            value={localValue}
            onChange={handleChange}
            autoComplete='off'
        />
    );
};

export default ControlledInput;